// Edit a project's own details — name, app URL, description — and the safety
// settings that decide whether the engine may WRITE to the app under test.
//
// Shared by the tester/company project page and the PM's project page so the
// two never drift. The backend (PATCH /project/{id}) already restricts these
// fields to project managers and admins; `canEdit` on the caller only decides
// whether the button is worth showing.

import { useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { updateProject } from "@/api/projects";

const FIELD_CLS =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white";

// The safety settings are TRI-state, not checkboxes: "" = follow the platform
// default, "on"/"off" = this project has decided. A checkbox cannot express the
// difference, and collapsing them would silently pin every project to whatever
// the default happened to be on the day someone opened this modal.
const INHERIT = "";

function tri(value) {
  if (value === null || value === undefined) return INHERIT;
  return value ? "on" : "off";
}

function fromTri(value) {
  if (value === INHERIT) return null;
  return value === "on";
}

export default function EditProjectModal({ open, project, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    base_url: "",
    description: "",
    probe: INHERIT,
    writes: INHERIT,
    demo: INHERIT,
    seconds: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const effective = project?.effective_settings || {};
  const stored = project?.settings || {};

  // Refill from the project every time the modal opens, so reopening after a
  // cancel shows what is actually stored rather than the abandoned draft.
  useEffect(() => {
    if (!open) return;
    setForm({
      name: project?.name || "",
      base_url: project?.base_url || "",
      description: project?.description || "",
      probe: tri(stored.crawl_probe_valid_submit),
      writes: tri(stored.crawl_probe_allow_writes),
      demo: tri(stored.demo_mode),
      seconds:
        stored.crawl_seconds_per_account == null
          ? ""
          : String(stored.crawl_seconds_per_account),
    });
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.name, project?.base_url, project?.description,
      stored.crawl_probe_valid_submit, stored.crawl_probe_allow_writes,
      stored.demo_mode, stored.crawl_seconds_per_account]);

  const urlChanged =
    !!project?.base_url && form.base_url.trim() !== project.base_url;

  const seconds = form.seconds.trim();
  const secondsInvalid =
    seconds !== "" && (!/^\d+(\.\d+)?$/.test(seconds) || Number(seconds) < 10 || Number(seconds) > 3600);

  // The create form validates the URL but this one accepted any string, and the
  // server doesn't check either — so a typo here silently pointed every future
  // crawl and run at nothing.
  const urlTrimmed = form.base_url.trim();
  const urlInvalid = urlTrimmed !== "" && !/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(urlTrimmed);

  // Records are only actually created when BOTH crawl switches are on. Showing the
  // warning for one of them would misreport the risk in the direction that matters.
  // Note demo_mode is NOT part of this: it governs whether generated cases may RUN
  // unattended, and never authorised the crawler to write while mapping the app.
  const resolveTri = (value, fallback) => {
    const chosen = fromTri(value);
    return chosen === null ? fallback : chosen;
  };
  const willWrite =
    resolveTri(form.probe, effective.crawl_probe_valid_submit) &&
    resolveTri(form.writes, effective.crawl_probe_allow_writes);

  async function submit(e) {
    e.preventDefault();
    if (secondsInvalid || urlInvalid) return;
    setError(null);
    setBusy(true);
    try {
      const saved = await updateProject(project.id, {
        name: form.name.trim(),
        base_url: form.base_url.trim(),
        description: form.description.trim(),
        // Explicit null is meaningful — it clears the override and returns the
        // project to the platform default.
        crawl_probe_valid_submit: fromTri(form.probe),
        crawl_probe_allow_writes: fromTri(form.writes),
        demo_mode: fromTri(form.demo),
        crawl_seconds_per_account: seconds === "" ? null : Number(seconds),
      });
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(err.message || "Could not save the project.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Edit project" size="md">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="edit-project-name" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Project name
          </label>
          <input
            id="edit-project-name"
            className={FIELD_CLS}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            maxLength={255}
          />
        </div>

        <div>
          <label htmlFor="edit-project-url" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
            App URL
          </label>
          <input
            id="edit-project-url"
            className={FIELD_CLS}
            value={form.base_url}
            placeholder="https://app.example.com"
            maxLength={2048}
            onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
          />
          {urlInvalid && (
            <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">
              Enter a full http:// or https:// address, e.g. https://app.example.com
            </p>
          )}
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            Use the site root, not the page you land on. Pointing this at a
            sub-page (e.g. <code>/sign-in</code>) makes the crawler build every
            discovered route underneath it.
          </p>
        </div>

        <div>
          <label htmlFor="edit-project-desc" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Description <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <textarea
            id="edit-project-desc"
            className={`${FIELD_CLS} min-h-[80px] resize-y`}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>

        <div className="space-y-4 rounded-xl border border-slate-200 p-4 dark:border-white/10">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              Safety
            </h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Controls whether the engine may change data in this app. Set these
              to <strong>Off</strong> for anything pointing at a customer&apos;s
              production system.
            </p>
          </div>

          <div>
            <label htmlFor="edit-project-demo" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Demo / sandbox environment
            </label>
            <select
              id="edit-project-demo"
              className={FIELD_CLS}
              value={form.demo}
              onChange={(e) => setForm((f) => ({ ...f, demo: e.target.value }))}
            >
              <option value={INHERIT}>
                Use platform default ({effective.demo_mode ? "on" : "off"})
              </option>
              <option value="on">On — throwaway data, cases may run unattended</option>
              <option value="off">Off — real data, park changes as manual</option>
            </select>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              When off, test cases that create, edit or delete data are marked
              manual instead of running on their own. Cases that send a real
              email or take a payment stay manual either way.
            </p>
          </div>

          <div>
            <label htmlFor="edit-project-probe" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Let the crawler submit forms
            </label>
            <select
              id="edit-project-probe"
              className={FIELD_CLS}
              value={form.probe}
              onChange={(e) => setForm((f) => ({ ...f, probe: e.target.value }))}
            >
              <option value={INHERIT}>
                Use platform default ({effective.crawl_probe_valid_submit ? "on" : "off"})
              </option>
              <option value="on">On — submit one real record per form</option>
              <option value="off">Off — never submit anything</option>
            </select>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              Submitting once per form is how the crawler learns the app&apos;s
              real success message, which makes happy-path cases verifiable. It
              also creates a real record each time.
            </p>
          </div>

          <div>
            <label htmlFor="edit-project-writes" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Allow writes to this app
            </label>
            <select
              id="edit-project-writes"
              className={FIELD_CLS}
              value={form.writes}
              onChange={(e) => setForm((f) => ({ ...f, writes: e.target.value }))}
            >
              <option value={INHERIT}>
                Use platform default ({effective.crawl_probe_allow_writes ? "on" : "off"})
              </option>
              <option value="on">On — this app is safe to create records in</option>
              <option value="off">Off — never write to this app</option>
            </select>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              The explicit authorisation. Both this and the setting above must be
              on before the crawler commits anything — wanting the success message
              and being allowed to create a record to get it are separate
              questions.
            </p>
          </div>

          {willWrite && (
            <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
              <ShieldAlert size={15} className="mt-px shrink-0" />
              <span>
                Crawling this project will create real records in the app. Fine
                for a sandbox — turn either setting off for a live system.
              </span>
            </p>
          )}

          <div>
            <label htmlFor="edit-project-seconds" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Crawl time per account{" "}
              <span className="font-normal text-slate-400">(seconds, optional)</span>
            </label>
            <input
              id="edit-project-seconds"
              className={FIELD_CLS}
              value={form.seconds}
              inputMode="numeric"
              placeholder={`Platform default — ${effective.crawl_seconds_per_account ?? 300}`}
              onChange={(e) => setForm((f) => ({ ...f, seconds: e.target.value }))}
            />
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              Raise this for a large app. A crawl that runs out of time stops
              quietly, and the pages it never reached simply produce no test
              cases — so a truncated crawl looks the same as a small app.
            </p>
            {secondsInvalid && (
              <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-300">
                Enter a number between 10 and 3600, or leave it blank to use the
                default.
              </p>
            )}
          </div>
        </div>

        {urlChanged && (
          <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
            Changing the app URL does not re-crawl. Pages, credentials and test
            cases keep the URLs they were discovered with — generate again to
            pick up the new one.
          </p>
        )}

        {error && (
          <p className="rounded-xl bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={busy || !form.name.trim() || !form.base_url.trim() || secondsInvalid || urlInvalid}
          >
            {busy ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
